import { IconGridDots } from '@tabler/icons-react';
import { SearchBar } from 'components/SearchBar/SearchBar';
import {
    Navbar as FlowbiteNavbar,
    NavbarBrand as FlowbiteNavbarBrand,
} from 'flowbite-react';
import HelpDropdown from 'components/HelpDropdown';
import { NavLink, useLocation } from 'react-router';
import UserDropdown from 'components/UserDropdown';
import { Spinner } from 'shared/Spinner';
import { useNavigationLinks } from 'utils/hooks/useNavigationLinks';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import { Brand } from 'components/Brand';

// references 'tailwind.config.js' - spacing.navbar
export const NAVBAR_HEIGHT = 70;

interface NavbarProps {
    isSidebarAvailable: boolean;
    isSidebarLoading: boolean;
    isSidebarOpen: boolean;
    setIsSidebarOpen: (open: boolean) => void;
}
const Navbar = ({
    isSidebarAvailable,
    isSidebarLoading,
    isSidebarOpen,
    setIsSidebarOpen,
}: NavbarProps) => {
    const { t } = useTranslation();
    const { navigationLinks } = useNavigationLinks();
    const { pathname } = useLocation();

    return (
        <FlowbiteNavbar
            className="fixed w-full z-(--z-navbar) h-navbar bg-gray-800 text-gray-50 !py-0"
            fluid
            theme={{ root: { inner: { base: 'h-full flex' } } }}
        >
            <div className="flex items-center w-full">
                <div className="flex gap-6">
                    <div className="flex gap-6 items-center">
                        {isSidebarAvailable ? (
                            <button
                                onClick={() => setIsSidebarOpen(!isSidebarOpen)}
                                aria-label={t(
                                    `layout:navigation-menu:${
                                        isSidebarOpen ? 'collapse' : 'expand'
                                    }`
                                )}
                                type="button"
                                className="hover:bg-gray-700 p-2 rounded-md"
                            >
                                <IconGridDots
                                    size={32}
                                    className={classNames({
                                        'stroke-primary-500': isSidebarOpen,
                                    })}
                                />
                            </button>
                        ) : null}

                        {isSidebarLoading ? <Spinner size="sm" /> : null}

                        {/*
                         * Hack `to` prop because apparently `flowbite-react`
                         * has problems with deriving props from `as` component.
                         */}
                        <FlowbiteNavbarBrand as={NavLink} {...{ to: '/' }}>
                            <div className="hidden xl:inline-block">
                                <Brand />
                            </div>
                            <div className="hidden lg:inline-block xl:hidden">
                                <Brand leaf />
                            </div>
                        </FlowbiteNavbarBrand>
                    </div>

                    <div className="hidden xxl:flex items-center justify-center gap-6">
                        {navigationLinks.map(
                            ({
                                id,
                                href,
                                text,
                                icon: Icon,
                                isActiveLink,
                                reloadOnActive,
                            }) => (
                                <NavLink
                                    key={id}
                                    to={href}
                                    reloadDocument={
                                        reloadOnActive && pathname === href
                                    }
                                    className="flex gap-2 items-center group"
                                >
                                    {({ isActive }) => (
                                        <>
                                            <Icon
                                                size={16}
                                                className={classNames({
                                                    'text-gray-50 group-hover:text-primary-500': true,
                                                    'text-primary-500':
                                                        isActive ||
                                                        isActiveLink?.(
                                                            pathname
                                                        ),
                                                })}
                                            />
                                            <span
                                                className={classNames({
                                                    'font-medium text-gray-50 group-hover:text-primary-500': true,
                                                    'text-primary-500':
                                                        isActive ||
                                                        isActiveLink?.(
                                                            pathname
                                                        ),
                                                })}
                                            >
                                                {text}
                                            </span>
                                        </>
                                    )}
                                </NavLink>
                            )
                        )}
                    </div>
                </div>

                <div className="grow flex justify-center">
                    <SearchBar />
                </div>

                <div className="flex items-center lg:gap-3">
                    <HelpDropdown />
                    <UserDropdown />
                </div>
            </div>
        </FlowbiteNavbar>
    );
};

export default Navbar;
